"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { storyblokEditable } from "@storyblok/react/rsc";
import Autoplay from "embla-carousel-autoplay";
import { fetchStoryblokList } from "@/lib/storyblok/fetchList";
import { useMembership } from "@/lib/hooks/useMembership";
import { useLightboxOpen } from "@/components/layout/LightboxProvider";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  GalleryNavPrevious,
  GalleryNavNext,
  type CarouselApi,
} from "@/components/ui/carousel";
import { ArticleCard } from "@/components/layout/ArticleCard";
import { GridContainer } from "@/components/layout/GridContainer";
import { ArticlePreviewModal, type ArticlePreviewData } from "@/components/landing/ArticlePreviewModal";
import { useIsLandingPage } from "@/lib/landing/LandingContext";
import { trackEvent } from "@/lib/analytics";
import type { ArticleStoryblok } from "@/component-types-sb";

// Background color options for the gallery
type GalleryBackgroundColor = "none" | "white" | "sand-100" | "sky-50";

const backgroundColorClasses: Record<GalleryBackgroundColor, string> = {
  none: "bg-transparent",
  white: "bg-white",
  "sand-100": "bg-sand-100",
  "sky-50": "bg-sky-50",
};

// Text colors based on background
const textColorClasses: Record<GalleryBackgroundColor, string> = {
  none: "text-black",
  white: "text-black",
  "sand-100": "text-black",
  "sky-50": "text-black",
};

// Inline type definition until Storyblok types are regenerated
type ArticleGalleryStoryblok = {
  title?: string;
  count?: number;
  tags?: (number | string)[];
  tag_mode?: "or" | "and";
  background_color?: GalleryBackgroundColor;
  _uid: string;
  component: "article_gallery";
  [k: string]: any;
};

type ArticleGalleryProps = {
  blok: ArticleGalleryStoryblok;
  excludeUid?: string;
  titleAlign?: "center" | "left";
};

type ArticleData = {
  title: string;
  image?: { src: string; alt: string };
  tags: string[];
  href: string;
  memberOnly: boolean;
  subtitle?: string;
  excerpt?: string;
};

const ArticleGallery = ({ blok, excludeUid, titleAlign = "center" }: ArticleGalleryProps) => {
  const isLanding = useIsLandingPage();
  const { isMember } = useMembership();
  const [articles, setArticles] = useState<ArticleData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [previewArticle, setPreviewArticle] = useState<ArticlePreviewData | null>(null);
  const [api, setApi] = useState<CarouselApi>();
  const [canScroll, setCanScroll] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const lightboxOpen = useLightboxOpen();
  const autoplayRef = useRef(
    Autoplay({
      delay: 3000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    }),
  );

  const count = blok.count || 3;
  const tags = blok.tags;
  const tagMode = blok.tag_mode || "or";
  const bgColor = blok.background_color || "none";
  const bgClass = backgroundColorClasses[bgColor];
  const textClass = textColorClasses[bgColor];

  // Fetch articles on mount
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const filterQuery: Record<string, any> = {};

        // If tags are provided, filter by them
        if (tags && tags.length > 0) {
          filterQuery.tags = { any_in_array: tags.map(String).join(",") };
        }

        // Exclude specific article (e.g. current article on detail page)
        if (excludeUid) {
          filterQuery._uid = { not_in: excludeUid };
        }

        const needsAndFilter = tagMode === "and" && tags && tags.length > 1;

        // Ueber den eigenen Proxy statt direkt gegen api.storyblok.com: so
        // teilen sich alle Besucher einen Cache-Eintrag, statt jeder einen
        // eigenen Storyblok-Request auszuloesen. version und
        // excluding_fields setzt der Server.
        const stories0 = await fetchStoryblokList({
          content_type: "article",
          sort_by: "first_published_at:desc",
          per_page: needsAndFilter ? 100 : count,
          ...(Object.keys(filterQuery).length > 0 && {
            filter_query: filterQuery,
          }),
        });

        if (stories0.length > 0) {
          let stories = stories0;

          if (needsAndFilter) {
            const requiredTags = new Set(tags!.map(String));
            stories = stories.filter((story: any) => {
              const storyTags = ((story.content as ArticleStoryblok).tags || []).map(String);
              return [...requiredTags].every((t) => storyTags.includes(t));
            }).slice(0, count);
          }

          const fetchedArticles = stories.map((story: any) => {
            const content = story.content as ArticleStoryblok;
            return {
              title: content.title,
              image: content.image?.filename
                ? {
                    src: content.image.filename,
                    alt: content.image.alt || content.title,
                  }
                : undefined,
              tags: (content.tags || []).map(String),
              href: `/${story.full_slug}`,
              memberOnly: content.member_access === "member_only",
              subtitle: content.subtitle,
              excerpt: (content as any).except,
            };
          });
          setArticles(fetchedArticles);
        }
      } catch (error) {
        console.error("Error fetching articles:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticles();
  }, [count, tags, tagMode, excludeUid]);

  const articleCount = articles.length;

  // Check if carousel can scroll (i.e., content overflows)
  const checkCanScroll = useCallback(() => {
    if (!api) return;
    const canScrollNext = api.canScrollNext();
    const canScrollPrev = api.canScrollPrev();
    setCanScroll(canScrollNext || canScrollPrev);
  }, [api]);

  useEffect(() => {
    if (!api) return;

    // Initial check
    checkCanScroll();

    // Re-check on resize
    api.on("resize", checkCanScroll);
    api.on("reInit", checkCanScroll);

    return () => {
      api.off("resize", checkCanScroll);
      api.off("reInit", checkCanScroll);
    };
  }, [api, checkCanScroll]);

  // Track viewport visibility
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Control autoplay based on canScroll and viewport visibility
  useEffect(() => {
    const autoplay = autoplayRef.current;
    if (!api) return;

    if (canScroll && isInView && !lightboxOpen) {
      autoplay.play();
    } else {
      autoplay.stop();
    }
  }, [api, canScroll, isInView, lightboxOpen]);

  // Show loading state
  if (isLoading) {
    return (
      <div {...storyblokEditable(blok)} className="w-full">
        <section
          className={`${bgClass} py-8 flex flex-col gap-4 w-full items-center rounded-3xl md:rounded-none`}
        >
          <h2 className={`text-headline-xl ${textClass} ${titleAlign === "left" ? "text-left pl-4 md:pl-8 lg:pl-0" : "text-center"} mb-2`}>
            {blok.title || "Die neuesten Artikel"}
          </h2>
          <div className="flex gap-4 px-4 md:px-8">
            {[...Array(Math.min(count, 3))].map((_, i) => (
              <div
                key={i}
                className="w-[278px] lg:w-[320px] h-[200px] bg-sand-100 rounded-2xl animate-pulse"
              />
            ))}
          </div>
        </section>
      </div>
    );
  }

  if (articleCount === 0) {
    return (
      <div {...storyblokEditable(blok)} className="w-full">
        <section
          className={`${bgClass} py-8 flex flex-col gap-4 w-full items-center rounded-3xl md:rounded-none`}
        >
          <h2 className={`text-headline-xl ${textClass} ${titleAlign === "left" ? "text-left pl-4 md:pl-8 lg:pl-0" : "text-center"} mb-2`}>
            {blok.title || "Die neuesten Artikel"}
          </h2>
          <p className={`text-copy text-center ${textClass} opacity-60`}>
            Keine Artikel gefunden
          </p>
        </section>
      </div>
    );
  }

  return (
    <div ref={sectionRef} {...storyblokEditable(blok)} className="w-full ">
      <section
        className={`${bgClass} py-8 flex flex-col gap-4 w-full items-center rounded-3xl md:rounded-none`}
      >
        {/* Header */}
        <h2 className={`text-headline-xl ${textClass} ${titleAlign === "left" ? "text-left pl-4 md:pl-8 lg:pl-0" : "text-center"} mb-2`}>
          {blok.title || "Die neuesten Artikel"}
        </h2>

        {/* Article Cards Carousel */}
        <GridContainer className="!px-0">
          <div className="col-span-12 lg:col-span-10 lg:col-start-2">
            <Carousel
              setApi={setApi}
              opts={{
                align: "center",
                loop: false,
                dragFree: false,
                watchResize: true,
                breakpoints: {
                  "(min-width: 768px)": {
                    align: "start",
                    dragFree: true,
                  },
                },
              }}
              plugins={[autoplayRef.current]}
              className="w-full relative"
            >
              <GalleryNavPrevious show={canScroll} />
              <CarouselContent
                className={
                  canScroll
                    ? "ml-0 pl-4 md:pl-8 lg:pl-0 items-stretch"
                    : "justify-center items-stretch"
                }
              >
                {articles.map((article, index) => (
                  <CarouselItem
                    key={`${article.href}-${index}`}
                    className={
                      canScroll
                        ? "pl-4 first:lg:pl-0 basis-auto h-auto card-rotate-container"
                        : "basis-auto px-2 h-auto card-rotate-container"
                    }
                  >
                    <ArticleCard
                      title={article.title}
                      image={article.image}
                      tags={article.tags}
                      href={article.href}
                      memberOnly={article.memberOnly}
                      isMember={isMember}
                      className="h-full"
                      onPreviewClick={isLanding ? () => {
                        setPreviewArticle({
                          title: article.title,
                          image: article.image,
                          tags: article.tags,
                          memberOnly: article.memberOnly,
                          subtitle: article.subtitle,
                          excerpt: article.excerpt,
                        });
                        trackEvent("content_preview_opened", {
                          type: "article",
                          title: article.title,
                          source: "article_gallery",
                        });
                      } : undefined}
                    />
                  </CarouselItem>
                ))}
                {/* Spacer for right padding */}
                {canScroll && <div className="shrink-0 w-4 md:w-8 lg:w-0" />}
              </CarouselContent>
              <GalleryNavNext show={canScroll} />
            </Carousel>
          </div>
        </GridContainer>
      </section>
      {previewArticle && (
        <ArticlePreviewModal article={previewArticle} onClose={() => setPreviewArticle(null)} />
      )}
    </div>
  );
};

export default ArticleGallery;
